import React, { useEffect, useState, useMemo } from "react";
import { auth, db } from "./firebase";
import { collection, onSnapshot, query, orderBy, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { Search, User, X, Pill, Phone, ChevronRight, Trash2, Calendar, Clock, Activity, History } from "lucide-react";

const PatientHistory = () => {
  const [history, setHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroup, setSelectedGroup] = useState(null);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const q = query(
      collection(db, "hospitals", user.uid, "patients"),
      orderBy("registeredAt", "desc")
    );

    return onSnapshot(q, (snapshot) => {
      setHistory(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
  }, []);

  // --- DELETE SINGLE VISIT ---
  const deleteVisit = async (patientId, visitDate) => {
    const user = auth.currentUser;
    if (!user || !window.confirm("Are you sure you want to delete this specific visit record?")) return;

    try {
      const patientRef = doc(db, "hospitals", user.uid, "patients", patientId);
      const patientData = history.find(p => p.id === patientId);
      
      const updatedVisits = patientData.visitHistory.filter(v => v.date !== visitDate);

      await updateDoc(patientRef, { visitHistory: updatedVisits });
      
      // Update the local state for immediate UI feedback in modal
      if (selectedGroup) {
        const updatedAllVisits = selectedGroup.allVisits.filter(v => v.date !== visitDate);
        setSelectedGroup({ ...selectedGroup, allVisits: updatedAllVisits });
      }
    } catch (err) {
      alert("Error deleting visit");
    }
  };

  // --- DELETE FULL PATIENT RECORD ---
  const deletePatient = async (patientDocId, name) => {
    const user = auth.currentUser;
    if (!user || !window.confirm(`WARNING: This will permanently delete ALL records for ${name}. Continue?`)) return;

    try {
      await deleteDoc(doc(db, "hospitals", user.uid, "patients", patientDocId));
      if (selectedGroup) {
        const updatedPatients = selectedGroup.patients.filter(p => p.id !== patientDocId);
        if (updatedPatients.length === 0) setSelectedGroup(null);
        else setSelectedGroup({ ...selectedGroup, patients: updatedPatients });
      }
    } catch (err) {
      alert("Error deleting patient");
    }
  };

  const groupedData = useMemo(() => {
    const groups = {};
    history.forEach(patient => {
      const phone = patient.phone || "No Phone";
      if (!groups[phone]) {
        groups[phone] = { phone, names: new Set(), patients: [], allVisits: [] };
      }
      groups[phone].names.add(patient.name);
      groups[phone].patients.push(patient);
      
      if (patient.visitHistory) {
        const validVisits = patient.visitHistory.map(v => ({ 
          ...v, 
          patientName: patient.name, 
          patientDocId: patient.id 
        }));
        groups[phone].allVisits.push(...validVisits);
      }
    });

    return Object.values(groups).map(group => ({
      ...group,
      allVisits: group.allVisits.sort((a, b) => new Date(b.date) - new Date(a.date)),
      displayNames: Array.from(group.names).join(", ")
    }));
  }, [history]);

  const filteredGroups = groupedData.filter(g =>
    g.displayNames.toLowerCase().includes(searchTerm.toLowerCase()) || g.phone.includes(searchTerm)
  );

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 bg-[#F8FAFC] min-h-screen font-sans antialiased text-slate-900">
      
      {/* HEADER SECTION */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-[900] tracking-tighter text-slate-900 uppercase italic">
            Medical <span className="text-blue-600">Archives</span>
          </h1>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Centralized Patient Records</p>
        </div>
        
        <div className="relative w-full md:w-96">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input 
            type="text" 
            placeholder="Search by Name or Phone..." 
            className="w-full pl-12 pr-4 py-3.5 bg-white border border-slate-200 rounded-[1.5rem] outline-none shadow-sm focus:border-blue-500 transition-all font-bold text-sm"
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* PATIENT GROUPS TABLE */}
      <div className="bg-white rounded-[2.5rem] border border-slate-100 overflow-hidden shadow-xl shadow-slate-200/50">
        <table className="w-full text-left">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest">Patient Details</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">History Size</th>
              <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">View</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {filteredGroups.map((group, idx) => (
              <tr key={idx} className="hover:bg-blue-50/30 transition-all group cursor-pointer" onClick={() => setSelectedGroup(group)}>
                <td className="px-8 py-5">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 bg-white border-2 border-slate-100 rounded-2xl flex items-center justify-center text-blue-600 shadow-sm group-hover:border-blue-200">
                      <Phone size={20} strokeWidth={2.5}/>
                    </div>
                    <div>
                      <p className="text-sm font-[900] text-slate-800 uppercase tracking-tight">{group.displayNames}</p>
                      <p className="text-xs font-bold text-slate-400">{group.phone}</p>
                    </div>
                  </div>
                </td>
                <td className="px-8 py-5 text-center">
                  <span className="px-4 py-1.5 rounded-xl bg-slate-100 text-slate-600 text-[10px] font-black uppercase tracking-tighter">
                    {group.allVisits.length} Records
                  </span>
                </td>
                <td className="px-8 py-5 text-right">
                  <button className="p-3 bg-slate-900 text-white rounded-2xl hover:bg-blue-600 transition-all shadow-lg active:scale-95">
                    <ChevronRight size={20} strokeWidth={3}/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredGroups.length === 0 && (
          <div className="py-24 text-center">
            <Search size={40} className="mx-auto text-slate-200 mb-4" />
            <p className="text-slate-400 font-black uppercase text-xs tracking-[0.2em]">No Records Found</p>
          </div>
        )}
      </div>

      {/* MODAL - NEAT TIMELINE VIEW */}
      {selectedGroup && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[3rem] w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col border border-white/20 animate-in zoom-in duration-300">
            
            {/* Modal Header */}
            <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <div className="flex items-center gap-4">
                <div className="bg-blue-600 p-3 rounded-2xl text-white shadow-lg shadow-blue-200">
                  <History size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-[900] text-slate-900 leading-none tracking-tighter italic uppercase">{selectedGroup.phone}</h2>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mt-1">Visit Timeline Archive</p>
                </div>
              </div>
              <button onClick={() => setSelectedGroup(null)} className="p-3 hover:bg-red-50 hover:text-red-500 rounded-full transition-all">
                <X size={24} strokeWidth={3} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 bg-white custom-scrollbar">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                
                {/* Left: Patient Profiles (Delete Option Here) */}
                <div className="lg:col-span-4 space-y-4">
                  <p className="text-[10px] font-black text-blue-600 uppercase tracking-[0.2em] px-1">Registered Profiles</p>
                  {selectedGroup.patients.map((p) => (
                    <div key={p.id} className="group flex justify-between items-center bg-slate-50 p-4 rounded-[1.5rem] border border-slate-100 transition-all hover:bg-white hover:shadow-md">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-white rounded-xl flex items-center justify-center text-slate-400 border border-slate-100"><User size={18}/></div>
                        <div>
                          <p className="text-sm font-black text-slate-800 uppercase tracking-tight leading-none mb-1">{p.name}</p>
                          <p className="text-[10px] font-bold text-slate-400">{p.age}Y • {p.gender}</p>
                        </div>
                      </div>
                      <button 
                        onClick={(e) => { e.stopPropagation(); deletePatient(p.id, p.name); }}
                        className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Right: History Timeline (Neat Cards) */}
                <div className="lg:col-span-8 space-y-8 relative before:absolute before:left-6 before:top-2 before:bottom-0 before:w-1 before:bg-slate-50">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] px-1 mb-6">Medical Timeline</p>
                  
                  {selectedGroup.allVisits.map((visit, idx) => (
                    <div key={idx} className="relative pl-14">
                      {/* Timeline Indicator */}
                      <div className="absolute left-4 top-0 w-5 h-5 bg-white border-4 border-blue-500 rounded-full z-10 shadow-sm"></div>
                      
                      <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-xl hover:border-blue-100 transition-all group">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-600 text-[10px] font-black rounded-lg uppercase">
                                <Calendar size={12}/> {new Date(visit.date).toLocaleDateString('en-GB')}
                              </span>
                              <span className="flex items-center gap-1.5 px-3 py-1 bg-slate-50 text-slate-500 text-[10px] font-black rounded-lg uppercase">
                                <Clock size={12}/> {new Date(visit.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <h4 className="text-lg font-[900] text-slate-900 uppercase tracking-tight italic">
                              {visit.diagnosis || "Medical Review"}
                            </h4>
                            <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase">Patient: <span className="text-slate-600">{visit.patientName}</span></p>
                          </div>
                          <button 
                            onClick={() => deleteVisit(visit.patientDocId, visit.date)}
                            className="opacity-0 group-hover:opacity-100 p-2.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all shadow-sm"
                          >
                            <X size={18} strokeWidth={3} />
                          </button>
                        </div>

                        {visit.complaints && (
                          <div className="mb-4 flex items-start gap-2 bg-slate-50/50 p-3 rounded-xl border border-dashed border-slate-200">
                            <Activity size={14} className="text-slate-400 mt-0.5" />
                            <p className="text-xs font-bold text-slate-500 italic">"{visit.complaints}"</p>
                          </div>
                        )}

                        {visit.medicines?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-50">
                            {visit.medicines.map((med, mid) => (
                              <div key={mid} className="bg-white border border-slate-200 px-3 py-2 rounded-[1rem] flex items-center gap-3 shadow-sm hover:border-blue-300 transition-colors">
                                <div className="p-1.5 bg-blue-50 text-blue-600 rounded-lg"><Pill size={12}/></div>
                                <div>
                                  <p className="text-[11px] font-[900] text-slate-800 uppercase tracking-tight">{med.name}</p>
                                  <p className="text-[9px] font-bold text-slate-400">{med.timing} • {med.duration}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientHistory;